import { useSettings } from "@hooks/useSettings";
import { ComponentProps } from "@ts/components";
import { BnplProviders } from "@ts/sanity";
import { MoneyV2 } from "@ts/shopify-storefront";
import React, { FC } from "react";

import { useGetFormattedSalePriceFromTagOrRange } from "~/hooks/useCartLegacy";

export type BuyNowPayLaterInputProps = ComponentProps & {
  compareAtPrice: MoneyV2;
  costBreakdownEnabled: boolean;
  onSale: boolean;
  presentmentPrice: MoneyV2;
  saleTag: string;
};

export type BuyNowPayLaterOutputProps = {
  className?: string;
  costBreakdown: number;
  costBreakdownProviders: BnplProviders[];
  label?: string;
  providers: BnplProviders[];
};

export const withBuyNowPayLater =
  (Component: FC<BuyNowPayLaterOutputProps>) =>
  ({
    name = "BuyNowPayLater",
    className,
    compareAtPrice,
    onSale,
    presentmentPrice,
    saleTag,
  }: BuyNowPayLaterInputProps) => {
    const { buyNowPayLater } = useSettings();

    const getFormattedSalePriceFromTagOrRange =
      useGetFormattedSalePriceFromTagOrRange();

    const { price, salePrice } = getFormattedSalePriceFromTagOrRange(
      presentmentPrice,
      saleTag,
      compareAtPrice,
    );
    const isDiscounted =
      Number(compareAtPrice?.amount) > Number(presentmentPrice?.amount);
    const actualPrice = onSale
      ? price?.replace(/\D+(\.?)/, "")
      : isDiscounted
        ? compareAtPrice?.amount
        : presentmentPrice?.amount;
    const actualSalePrice = onSale
      ? salePrice?.replace(/\D+(\.?)/, "")
      : isDiscounted
        ? presentmentPrice?.amount
        : compareAtPrice?.amount;
    const actualPriceNumber = Number(actualPrice);
    const actualSalePriceNumber = Number(actualSalePrice);
    if (isNaN(actualPriceNumber) && isNaN(actualSalePriceNumber)) {
      return null;
    }
    const costBreakdown =
      isDiscounted || onSale ? actualSalePrice / 4 : actualPrice / 4;
    const providers: BnplProviders[] = [];
    const costBreakdownProviders: BnplProviders[] = [];
    buyNowPayLater?.providers?.forEach((provider) => {
      if (provider.costBreakdownEnabled) {
        costBreakdownProviders.push(provider);
      } else {
        providers.push(provider);
      }
    });

    Component.displayName = name;
    return (
      <Component
        providers={providers}
        label={buyNowPayLater?.label}
        className={className}
        costBreakdown={costBreakdown}
        costBreakdownProviders={costBreakdownProviders}
      />
    );
  };
